import { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Button,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Tooltip,
  Divider,
  Stack,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Autocomplete,
  Tabs,
  Tab
} from '@mui/material';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import { adminAPI } from '../services/api';
import { PageHeader, EmptyState } from '../components/ui';
import { formatAdminInr } from '../utils/formatAdminPrice';

const DATE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'this_week', label: 'This Week' },
  { id: 'last_week', label: 'Last Week' },
  { id: 'this_month', label: 'This Month' },
  { id: 'last_month', label: 'Last Month' },
  { id: 'fy_current', label: 'Current FY (2026-27)' },
  { id: 'fy_previous', label: 'Last FY (2025-26)' },
  { id: 'custom', label: 'Custom Range' }
];

const compactMenuProps = {
  PaperProps: {
    sx: {
      maxHeight: 280,
      '& .MuiMenuItem-root': {
        py: 0.5,
        minHeight: 30,
        fontSize: '12.5px'
      },
      '& .MuiCheckbox-root': {
        py: 0.25,
        px: 0.5
      },
      '& .MuiListItemText-primary': {
        fontSize: '12.5px'
      }
    }
  }
};

export default function CustomReports() {
  const [activeTab, setActiveTab] = useState('finance'); // 'finance' | 'operations'
  const [reportType, setReportType] = useState('registrations');
  const [datePreset, setDatePreset] = useState('this_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [groupBy, setGroupBy] = useState('day');

  // Multiple selection for Treks & Booking Statuses
  const [selectedTreks, setSelectedTreks] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [compareYoY, setCompareYoY] = useState(false);

  // Pagination state (0-indexed for MUI, 1-indexed for backend)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // 1. Fetch Filter Options (Treks, Categories, Statuses)
  const { data: filterOptionsData } = useQuery(
    ['admin-report-filter-options'],
    async () => {
      const res = await adminAPI.getReportFilterOptions();
      return res.data?.data || {};
    },
    { staleTime: 10 * 60 * 1000 }
  );

  const treks = filterOptionsData?.treks || [];
  const categories = filterOptionsData?.categories || [];
  const statuses = filterOptionsData?.statuses || [];

  // Map trek id to name for rendering multiple selected
  const trekMap = useMemo(() => {
    const map = new Map();
    treks.forEach((t) => map.set(t.id, t.name));
    return map;
  }, [treks]);

  const statusMap = useMemo(() => {
    const map = new Map();
    statuses.forEach((s) => map.set(s.value, s.label));
    return map;
  }, [statuses]);

  // 2. Fetch Report Data
  const reportPayload = useMemo(() => {
    return {
      reportType,
      datePreset,
      startDate: datePreset === 'custom' ? startDate : undefined,
      endDate: datePreset === 'custom' ? endDate : undefined,
      groupBy,
      filters: {
        trekIds: selectedTreks.length > 0 ? selectedTreks : undefined,
        categoryId: selectedCategory || undefined,
        statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined
      },
      compareYoY,
      page: page + 1,
      limit: rowsPerPage
    };
  }, [reportType, datePreset, startDate, endDate, groupBy, selectedTreks, selectedCategory, selectedStatuses, compareYoY, page, rowsPerPage]);

  const { data: reportData, isLoading, isFetching, refetch } = useQuery(
    ['admin-custom-report', reportPayload],
    async () => {
      const res = await adminAPI.generateCustomReport(reportPayload);
      return res.data?.data || {};
    },
    { keepPreviousData: true }
  );

  const summary = reportData?.summary || {};
  const chartData = reportData?.chartData || [];
  const yoyChartData = reportData?.yoyChartData || [];
  const tableData = reportData?.tableData || [];
  const pagination = reportData?.pagination || { totalRows: 0 };
  const meta = reportData?.meta || {};

  const currentYearDigits = meta.currentYear || new Date().getFullYear();
  const prevYearDigits = meta.prevYear || (currentYearDigits - 1);
  const yearComparisonLabel = meta.yearComparisonLabel || `${currentYearDigits} vs ${prevYearDigits}`;

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // CSV Export Handler
  const exportToCSV = () => {
    if (!tableData || tableData.length === 0) return;

    if (activeTab === 'operations') {
      if (compareYoY) {
        const headers = [
          groupBy === 'trek_date' ? 'Trek Name' : (groupBy === 'trek' ? 'Trek Name' : 'Date / Period'),
          ...(groupBy === 'trek_date' ? ['Date'] : []),
          `Confirmed Paid (${currentYearDigits})`,
          `Confirmed Paid (${prevYearDigits})`,
          'Confirmed YoY Growth (%)',
          `Total Registrations (${currentYearDigits})`,
          `Total Registrations (${prevYearDigits})`,
          'Registrations YoY Growth (%)',
          `Pending (${currentYearDigits})`,
          `Pending (${prevYearDigits})`,
          `Cancelled (${currentYearDigits})`,
          `Cancelled (${prevYearDigits})`,
          `Conversion Rate % (${currentYearDigits})`,
          `Conversion Rate % (${prevYearDigits})`
        ];

        const rows = tableData.map((r) => [
          groupBy === 'trek_date' ? `"${r.trek_name || r.dimension_key}"` : `"${r.dimension_key}"`,
          ...(groupBy === 'trek_date' ? [`"${r.date_label || ''}"`] : []),
          r.confirmed_count || 0,
          r.yoy_confirmed_count || 0,
          r.confirmed_growth_pct !== null && r.confirmed_growth_pct !== undefined ? `${r.confirmed_growth_pct}%` : 'N/A',
          r.total_registrations || 0,
          r.yoy_total_registrations || 0,
          r.reg_growth_pct !== null && r.reg_growth_pct !== undefined ? `${r.reg_growth_pct}%` : 'N/A',
          r.pending_count || 0,
          r.yoy_pending_count || 0,
          r.cancelled_count || 0,
          r.yoy_cancelled_count || 0,
          r.conversion_rate || 0,
          r.yoy_conversion_rate || 0
        ]);

        const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `invincible_operations_yoy_${datePreset}_${groupBy}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      const headers = [
        groupBy === 'trek_date' ? 'Trek Name' : (groupBy === 'trek' ? 'Trek Name' : 'Date / Period'),
        ...(groupBy === 'trek_date' ? ['Date'] : []),
        'Total Registrations (Intents)',
        'Confirmed / Paid Bookings',
        'Pending',
        'Cancelled',
        'Conversion Rate (%)'
      ];

      const rows = tableData.map((r) => [
        groupBy === 'trek_date' ? `"${r.trek_name || r.dimension_key}"` : `"${r.dimension_key}"`,
        ...(groupBy === 'trek_date' ? [`"${r.date_label || ''}"`] : []),
        r.total_registrations || 0,
        r.confirmed_count || 0,
        r.pending_count || 0,
        r.cancelled_count || 0,
        r.conversion_rate || 0
      ]);

      const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invincible_operations_${datePreset}_${groupBy}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    if (compareYoY) {
      const headers = [
        groupBy === 'trek' ? 'Trek Name' : 'Date / Period',
        `Confirmed Paid (${currentYearDigits})`,
        `Confirmed Paid (${prevYearDigits})`,
        'Confirmed YoY Growth (%)',
        `Total Collected INR (${currentYearDigits})`,
        `Total Collected INR (${prevYearDigits})`,
        'Revenue YoY Growth (%)',
        `Total Intents (${currentYearDigits})`,
        `Total Intents (${prevYearDigits})`,
        'Intents YoY Growth (%)',
        `Online Revenue INR (${currentYearDigits})`,
        `Online Revenue INR (${prevYearDigits})`,
        `Cash Revenue INR (${currentYearDigits})`,
        `Cash Revenue INR (${prevYearDigits})`,
        `QR Revenue INR (${currentYearDigits})`,
        `QR Revenue INR (${prevYearDigits})`,
        `Conversion Rate % (${currentYearDigits})`,
        `Conversion Rate % (${prevYearDigits})`,
        `Remaining Due INR (${currentYearDigits})`,
        `Remaining Due INR (${prevYearDigits})`
      ];

      const rows = tableData.map((r) => [
        `"${r.dimension_key}"`,
        r.confirmed_count || 0,
        r.yoy_confirmed_count || 0,
        r.confirmed_growth_pct !== null && r.confirmed_growth_pct !== undefined ? `${r.confirmed_growth_pct}%` : 'N/A',
        r.collected_revenue || 0,
        r.yoy_collected_revenue || 0,
        r.revenue_growth_pct !== null && r.revenue_growth_pct !== undefined ? `${r.revenue_growth_pct}%` : 'N/A',
        r.total_registrations || 0,
        r.yoy_total_registrations || 0,
        r.reg_growth_pct !== null && r.reg_growth_pct !== undefined ? `${r.reg_growth_pct}%` : 'N/A',
        r.online_revenue || 0,
        r.yoy_online_revenue || 0,
        r.office_revenue || 0,
        r.yoy_office_revenue || 0,
        r.qr_revenue || 0,
        r.yoy_qr_revenue || 0,
        r.conversion_rate || 0,
        r.yoy_conversion_rate || 0,
        r.remaining_due || 0,
        r.yoy_remaining_due || 0
      ]);

      const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invincible_finance_yoy_report_${datePreset}_${groupBy}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    const headers = [
      groupBy === 'trek' ? 'Trek Name' : 'Date / Period',
      'Total Registrations',
      'Confirmed Paid',
      'Pending',
      'Cancelled',
      'Conversion Rate (%)',
      'Online Revenue (INR)',
      'Cash Revenue (INR)',
      'QR Bank Revenue (INR)',
      'Total Collected Revenue (INR)',
      'Remaining Due (INR)',
      'Total Order Value (INR)'
    ];

    const rows = tableData.map((r) => [
      `"${r.dimension_key}"`,
      r.total_registrations || 0,
      r.confirmed_count || 0,
      r.pending_count || 0,
      r.cancelled_count || 0,
      r.conversion_rate || 0,
      r.online_revenue || 0,
      r.office_revenue || 0,
      r.qr_revenue || 0,
      r.collected_revenue || 0,
      r.remaining_due || 0,
      r.total_order_value || 0
    ]);

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `invincible_finance_report_${datePreset}_${groupBy}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Chart max value for scaling
  const maxChartVal = useMemo(() => {
    let max = 1;
    chartData.forEach((d) => {
      const v = Number(d.total_count || 0);
      if (v > max) max = v;
    });
    if (compareYoY) {
      yoyChartData.forEach((d) => {
        const v = Number(d.total_count || 0);
        if (v > max) max = v;
      });
    }
    return max;
  }, [chartData, yoyChartData, compareYoY]);

  return (
    <Box sx={{ pb: 6 }}>
      {/* Header */}
      <PageHeader
        title="Custom Reports & Analytics"
        subtitle="Generate on-demand operational and financial reports with multi-select filters, payment mode breakdown, and year-over-year comparisons."
        action={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshRoundedIcon />}
              onClick={() => refetch()}
              disabled={isFetching}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<FileDownloadRoundedIcon />}
              onClick={exportToCSV}
              disabled={!tableData || tableData.length === 0}
            >
              Export CSV
            </Button>
          </Stack>
        }
      />

      {/* Module Navigation Tabs: Finance vs Operations/Sales */}
      <Paper sx={{ mb: 2.5, borderRadius: 2, bgcolor: '#ffffff', px: 2, pt: 0.5 }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => {
            setActiveTab(val);
            setPage(0);
          }}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab
            value="finance"
            icon={<AccountBalanceWalletRoundedIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Finance & Revenue"
            sx={{ fontWeight: 600, fontSize: '13.5px', textTransform: 'none', py: 1.5 }}
          />
          <Tab
            value="operations"
            icon={<GroupsRoundedIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Operations / Sales (Participants)"
            sx={{ fontWeight: 600, fontSize: '13.5px', textTransform: 'none', py: 1.5 }}
          />
        </Tabs>
      </Paper>

      {/* Date Presets Selector */}
      <Paper sx={{ p: 2, mb: 2.5, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarMonthRoundedIcon fontSize="small" color="primary" /> Date Presets
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {DATE_PRESETS.map((p) => {
            const isSelected = datePreset === p.id;
            return (
              <Chip
                key={p.id}
                label={p.label}
                clickable
                color={isSelected ? 'primary' : 'default'}
                variant={isSelected ? 'filled' : 'outlined'}
                onClick={() => {
                  setDatePreset(p.id);
                  setPage(0);
                }}
                sx={{ fontWeight: isSelected ? 600 : 400 }}
              />
            );
          })}
        </Stack>

        {datePreset === 'custom' && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }} alignItems="center">
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', sm: 200 } }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', sm: 200 } }}
            />
          </Stack>
        )}
      </Paper>

      {/* Filters & Grouping Bar with Multi-Select */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterAltRoundedIcon fontSize="small" color="primary" /> Multi-Select Filters & Dimensions
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          {/* Group By Dimension */}
          <FormControl size="small" sx={{ minWidth: 155 }}>
            <InputLabel sx={{ fontSize: '13px' }}>Group By</InputLabel>
            <Select
              value={groupBy}
              label="Group By"
              MenuProps={compactMenuProps}
              sx={{ fontSize: '13px' }}
              onChange={(e) => {
                setGroupBy(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="day" sx={{ py: 0.5, minHeight: 30, fontSize: '12.5px' }}>By Day</MenuItem>
              <MenuItem value="trek_date" sx={{ py: 0.5, minHeight: 30, fontSize: '12.5px' }}>By Trek + Date</MenuItem>
              <MenuItem value="trek" sx={{ py: 0.5, minHeight: 30, fontSize: '12.5px' }}>By Trek</MenuItem>
              <MenuItem value="week" sx={{ py: 0.5, minHeight: 30, fontSize: '12.5px' }}>By Week</MenuItem>
              <MenuItem value="month" sx={{ py: 0.5, minHeight: 30, fontSize: '12.5px' }}>By Month</MenuItem>
            </Select>
          </FormControl>

          {/* Searchable Multi-Select Trek Selection */}
          <Autocomplete
            multiple
            size="small"
            options={treks}
            disableCloseOnSelect
            getOptionLabel={(option) => (typeof option === 'string' ? (trekMap.get(option) || option) : option.name || '')}
            isOptionEqualToValue={(option, value) => {
              const optId = typeof option === 'object' ? option.id : option;
              const valId = typeof value === 'object' ? value.id : value;
              return optId === valId;
            }}
            value={treks.filter((t) => selectedTreks.includes(t.id))}
            onChange={(event, newValue) => {
              setSelectedTreks(newValue.map((item) => (typeof item === 'object' ? item.id : item)));
              setPage(0);
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.id} style={{ padding: '3px 8px', minHeight: 30, fontSize: '12.5px' }}>
                <Checkbox
                  size="small"
                  checked={selected}
                  sx={{ p: 0.25, mr: 0.75 }}
                />
                <span style={{ fontSize: '12.5px' }}>{option.name}</span>
              </li>
            )}
            renderTags={(value, getTagProps) => {
              if (value.length === 0) return null;
              if (value.length <= 1) {
                return value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name}
                    size="small"
                    sx={{ height: 22, fontSize: '11px', mr: 0.5 }}
                  />
                ));
              }
              return (
                <Chip
                  label={`${value.length} Treks Selected`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '11px' }}
                />
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Treks (Search & Multi)"
                placeholder={selectedTreks.length === 0 ? "Search treks..." : ""}
                size="small"
                InputLabelProps={{ sx: { fontSize: '13px' } }}
              />
            )}
            sx={{ minWidth: 260, maxWidth: 360 }}
            ListboxProps={{
              style: { maxHeight: 280, fontSize: '12.5px' }
            }}
          />

          {/* Multiple Booking Status Selection */}
          <FormControl size="small" sx={{ minWidth: 200, maxWidth: 320 }}>
            <InputLabel sx={{ fontSize: '13px' }}>Booking Statuses (Multi)</InputLabel>
            <Select
              multiple
              value={selectedStatuses}
              input={<OutlinedInput label="Booking Statuses (Multi)" />}
              MenuProps={compactMenuProps}
              sx={{ fontSize: '13px' }}
              renderValue={(selected) => {
                if (selected.length === 0) return 'All Statuses';
                return selected.map((s) => statusMap.get(s) || s).join(', ');
              }}
              onChange={(e) => {
                const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                setSelectedStatuses(val);
                setPage(0);
              }}
            >
              {statuses.map((s) => (
                <MenuItem key={s.value} value={s.value} sx={{ py: 0.5, minHeight: 30, fontSize: '12.5px' }}>
                  <Checkbox checked={selectedStatuses.indexOf(s.value) > -1} size="small" sx={{ py: 0.25, px: 0.5 }} />
                  <ListItemText primary={s.label} primaryTypographyProps={{ fontSize: '12.5px' }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Category Filter */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontSize: '13px' }}>Category</InputLabel>
            <Select
              value={selectedCategory}
              label="Category"
              MenuProps={compactMenuProps}
              sx={{ fontSize: '13px' }}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="" sx={{ py: 0.5, minHeight: 30, fontSize: '12.5px' }}>All Categories</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id} sx={{ py: 0.5, minHeight: 30, fontSize: '12.5px' }}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* YoY Toggle with Year Digits */}
          <FormControlLabel
            control={
              <Switch
                checked={compareYoY}
                onChange={(e) => setCompareYoY(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 600, color: compareYoY ? 'primary.main' : 'text.primary' }}>
                Compare vs Last Year ({yearComparisonLabel})
              </Typography>
            }
          />
        </Stack>

        {/* Selected Treks Chips Display */}
        {selectedTreks.length > 0 && (
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Filtered Treks:</Typography>
            {selectedTreks.map((tid) => (
              <Chip
                key={tid}
                label={trekMap.get(tid) || tid}
                size="small"
                onDelete={() => {
                  setSelectedTreks((prev) => prev.filter((id) => id !== tid));
                  setPage(0);
                }}
                color="primary"
                variant="outlined"
              />
            ))}
            <Button
              size="small"
              onClick={() => {
                setSelectedTreks([]);
                setPage(0);
              }}
              sx={{ fontSize: '11px', textTransform: 'none', py: 0 }}
            >
              Clear all
            </Button>
          </Box>
        )}
      </Paper>

      {/* KPI Cards Row (Conditional on activeTab) */}
      {activeTab === 'operations' ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 3
          }}
        >
          {/* Total Registration Intents */}
          <Card sx={{ borderRadius: 2, borderLeft: '4px solid #1976d2' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Total Registration Intents
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, my: 0.5, color: '#1976d2' }}>
                {isLoading ? '...' : (summary.totalRegistrations || 0).toLocaleString('en-IN')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total bookings & enrollments initiated
              </Typography>
            </CardContent>
          </Card>

          {/* Confirmed Paid Participants */}
          <Card sx={{ borderRadius: 2, borderLeft: '4px solid #2e7d32' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Confirmed Paid Participants
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ my: 0.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {isLoading ? '...' : (summary.confirmedCount || 0).toLocaleString('en-IN')}
                </Typography>
                <Chip
                  label={`${summary.conversionRate || 0}% Conv.`}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontWeight: 600, height: 22 }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Actual enrolled & confirmed participants
              </Typography>
            </CardContent>
          </Card>

          {/* Pending Registrations */}
          <Card sx={{ borderRadius: 2, borderLeft: '4px solid #ed6c02' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Pending Registrations
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ my: 0.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#ed6c02' }}>
                  {isLoading ? '...' : (summary.pendingCount || 0).toLocaleString('en-IN')}
                </Typography>
                <Chip
                  label={`${summary.totalRegistrations > 0 ? ((summary.pendingCount / summary.totalRegistrations) * 100).toFixed(1) : 0}% of intents`}
                  size="small"
                  sx={{ fontWeight: 600, height: 22, bgcolor: '#fff3e0', color: '#e65100' }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Awaiting payment completion
              </Typography>
            </CardContent>
          </Card>

          {/* Cancelled Registrations */}
          <Card sx={{ borderRadius: 2, borderLeft: '4px solid #d32f2f' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Cancelled Registrations
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ my: 0.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                  {isLoading ? '...' : (summary.cancelledCount || 0).toLocaleString('en-IN')}
                </Typography>
                <Chip
                  label={`${summary.totalRegistrations > 0 ? ((summary.cancelledCount / summary.totalRegistrations) * 100).toFixed(1) : 0}% dropped`}
                  size="small"
                  sx={{ fontWeight: 600, height: 22, bgcolor: '#ffebee', color: '#c62828' }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Cancelled / dropped before confirmation
              </Typography>
            </CardContent>
          </Card>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 3
          }}
        >
          {/* Total Bookings */}
          <Card sx={{ borderRadius: 2, borderLeft: '4px solid #1976d2' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Total Registration Intents
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, my: 0.5 }}>
                {isLoading ? '...' : (summary.totalRegistrations || 0).toLocaleString('en-IN')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pending: {(summary.pendingCount || 0).toLocaleString('en-IN')} | Cancelled: {(summary.cancelledCount || 0).toLocaleString('en-IN')}
              </Typography>
            </CardContent>
          </Card>

          {/* Confirmed Seats & Conversion */}
          <Card sx={{ borderRadius: 2, borderLeft: '4px solid #2e7d32' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Confirmed Paid Bookings
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ my: 0.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {isLoading ? '...' : (summary.confirmedCount || 0).toLocaleString('en-IN')}
                </Typography>
                <Chip
                  label={`${summary.conversionRate || 0}% Conv.`}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontWeight: 600, height: 22 }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Paid seats ratio from total intents
              </Typography>
            </CardContent>
          </Card>

          {/* Collected Revenue by Mode */}
          <Card sx={{ borderRadius: 2, borderLeft: '4px solid #ed6c02' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Collected Revenue (By Mode)
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#ed6c02', my: 0.5 }}>
                {isLoading ? '...' : formatAdminInr(summary.totalCollectedRevenue || 0)}
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                <Chip
                  icon={<PaymentsRoundedIcon style={{ fontSize: 13 }} />}
                  label={`Online: ${formatAdminInr(summary.onlineRevenue || 0)}`}
                  size="small"
                  sx={{ height: 20, fontSize: '10px', fontWeight: 600, bgcolor: '#fff3e0' }}
                />
                <Chip
                  icon={<StoreRoundedIcon style={{ fontSize: 13 }} />}
                  label={`Cash: ${formatAdminInr(summary.officeRevenue || 0)}`}
                  size="small"
                  sx={{ height: 20, fontSize: '10px', fontWeight: 600, bgcolor: '#ede7f6' }}
                />
                <Chip
                  icon={<QrCode2RoundedIcon style={{ fontSize: 13 }} />}
                  label={`QR/Bank: ${formatAdminInr(summary.qrRevenue || 0)}`}
                  size="small"
                  sx={{ height: 20, fontSize: '10px', fontWeight: 600, bgcolor: '#e0f2f1', color: '#00695c' }}
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Remaining Dues & Ticket Size */}
          <Card sx={{ borderRadius: 2, borderLeft: '4px solid #9c27b0' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Average Ticket Size
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#9c27b0', my: 0.5 }}>
                {isLoading ? '...' : formatAdminInr(summary.avgTicketSize || 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pending Dues: {formatAdminInr(summary.totalRemainingDue || 0)} | Total Value: {formatAdminInr(summary.totalOrderValue || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Visual Trend Chart with Exact Year Digits */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            {activeTab === 'operations' ? <GroupsRoundedIcon color="primary" /> : <TrendingUpRoundedIcon color="primary" />}
            {activeTab === 'operations' ? 'Participant Volume Trend' : 'Visual Volume & Revenue Trend'} ({groupBy.toUpperCase()})
          </Typography>
          {compareYoY && (
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#1976d2', borderRadius: 0.5 }} />
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Year {currentYearDigits} (Current)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#90caf9', borderRadius: 0.5 }} />
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Year {prevYearDigits} (Last Year)</Typography>
              </Box>
            </Stack>
          )}
        </Stack>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={36} />
          </Box>
        ) : chartData.length === 0 ? (
          <EmptyState message="No registration or participant data found for this selection." />
        ) : (
          <Box sx={{ width: '100%', overflowX: 'auto', pt: 1, pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, height: 180, minWidth: chartData.length * 45 }}>
              {chartData.map((d, idx) => {
                const totalVal = Number(d.total_count || 0);
                const confirmedVal = Number(d.confirmed_count || 0);
                const heightPct = Math.max(4, Math.round((totalVal / maxChartVal) * 100));
                const confirmedHeightPct = totalVal > 0 ? Math.round((confirmedVal / totalVal) * 100) : 0;

                const yoyMatch = yoyChartData[idx];
                const yoyVal = yoyMatch ? Number(yoyMatch.total_count || 0) : 0;
                const yoyHeightPct = Math.max(4, Math.round((yoyVal / maxChartVal) * 100));

                return (
                  <Tooltip
                    key={d.label || idx}
                    title={
                      <Box sx={{ p: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>{d.label}</Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>{currentYearDigits} Intents: {totalVal}</Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: '#81c784' }}>{currentYearDigits} Confirmed Paid: {confirmedVal}</Typography>
                        {activeTab === 'operations' ? (
                          <Typography variant="caption" sx={{ display: 'block', color: '#90caf9', fontWeight: 600 }}>
                            Conversion: {totalVal > 0 ? ((confirmedVal / totalVal) * 100).toFixed(1) : 0}%
                          </Typography>
                        ) : (
                          <>
                            <Typography variant="caption" sx={{ display: 'block', color: '#ffb74d' }}>{currentYearDigits} Revenue: {formatAdminInr(d.revenue)}</Typography>
                            {(Number(d.online_revenue) > 0 || Number(d.office_revenue) > 0 || Number(d.qr_revenue) > 0) && (
                              <Typography variant="caption" sx={{ display: 'block', color: '#e0e0e0', fontSize: '9px', mt: 0.3 }}>
                                Online: {formatAdminInr(d.online_revenue || 0)} | Cash: {formatAdminInr(d.office_revenue || 0)} | QR: {formatAdminInr(d.qr_revenue || 0)}
                              </Typography>
                            )}
                          </>
                        )}
                        {compareYoY && (
                          <Box sx={{ mt: 0.5, borderTop: '1px dashed #666', pt: 0.5 }}>
                            <Typography variant="caption" sx={{ display: 'block', color: '#90caf9' }}>
                              {prevYearDigits} Intents: {yoyVal}
                            </Typography>
                            {yoyMatch && (
                              <Typography variant="caption" sx={{ display: 'block', color: '#a5d6a7' }}>
                                {prevYearDigits} Confirmed Paid: {Number(yoyMatch.confirmed_count || 0)}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    }
                  >
                    <Box sx={{ flex: 1, minWidth: 32, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 140, width: '100%' }}>
                        {/* Current Period Bar */}
                        <Box
                          sx={{
                            flex: 1,
                            height: `${heightPct}%`,
                            bgcolor: '#e3f2fd',
                            borderRadius: '4px 4px 0 0',
                            position: 'relative',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.85 }
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: `${confirmedHeightPct}%`,
                              bgcolor: '#1976d2'
                            }}
                          />
                        </Box>

                        {/* YoY Comparison Bar */}
                        {compareYoY && (
                          <Box
                            sx={{
                              flex: 1,
                              height: `${yoyHeightPct}%`,
                              bgcolor: '#90caf9',
                              borderRadius: '4px 4px 0 0',
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.85 }
                            }}
                          />
                        )}
                      </Box>

                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '10px',
                          color: 'text.secondary',
                          mt: 1,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: 55,
                          textAlign: 'center'
                        }}
                      >
                        {d.label?.slice(-5) || d.label}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Paginated Data Breakdown Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {activeTab === 'operations'
              ? (groupBy === 'trek_date' ? 'Daily Paid vs Registration by Trek + Date' : groupBy === 'day' ? 'Daily Paid vs Registration by Date' : 'Participant & Conversion Breakdown')
              : 'Detailed Financial Breakdown Table (with Payment Mode Breakdown)'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Showing {tableData.length} of {pagination.totalRows} rows (Server Paginated)
          </Typography>
        </Box>
        <Divider />

        <TableContainer sx={{ maxHeight: 540 }}>
          <Table stickyHeader size="small">
            <TableHead>
              {activeTab === 'operations' ? (
                compareYoY ? (
                  <TableRow>
                    {groupBy === 'trek_date' ? (
                      <>
                        <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', minWidth: 160 }}>Trek Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', minWidth: 110 }}>Date</TableCell>
                      </>
                    ) : (
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', minWidth: 150 }}>
                        {groupBy === 'trek' ? 'Trek Name' : 'Date / Period'}
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: 'success.main', minWidth: 150 }}>
                      Confirmed Paid ({yearComparisonLabel})
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc', minWidth: 140 }}>
                      Total Intents ({yearComparisonLabel})
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc', minWidth: 110 }}>
                      Pending ({yearComparisonLabel})
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: 'error.main', minWidth: 110 }}>
                      Cancelled ({yearComparisonLabel})
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc', minWidth: 90 }}>
                      Conv. %
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    {groupBy === 'trek_date' ? (
                      <>
                        <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', minWidth: 160 }}>Trek Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', minWidth: 110 }}>Date</TableCell>
                      </>
                    ) : (
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                        {groupBy === 'trek' ? 'Trek Name' : 'Date / Period'}
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Total Registration Intents</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: 'success.main' }}>Confirmed / Paid Bookings</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Pending</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: 'error.main' }}>Cancelled</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Conversion %</TableCell>
                  </TableRow>
                )
              ) : compareYoY ? (
                <TableRow>
                  {groupBy === 'trek_date' ? (
                    <>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', minWidth: 160 }}>Trek Name</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', minWidth: 110 }}>Date</TableCell>
                    </>
                  ) : (
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', minWidth: 150 }}>
                      {groupBy === 'trek' ? 'Trek Name' : 'Date / Period'}
                    </TableCell>
                  )}
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: 'success.main', minWidth: 140 }}>
                    Confirmed Paid ({yearComparisonLabel})
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#ed6c02', minWidth: 160 }}>
                    Total Collected ({yearComparisonLabel})
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc', minWidth: 120 }}>
                    Total Intents
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#fff8e1', color: '#b78103', minWidth: 130 }}>
                    Online (Razorpay)
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f3e5f5', color: '#6a1b9a', minWidth: 120 }}>
                    Cash
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#e0f2f1', color: '#00695c', minWidth: 110 }}>
                    QR / Bank
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc', minWidth: 90 }}>
                    Conv. %
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc', minWidth: 120 }}>
                    Remaining Due
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  {groupBy === 'trek_date' ? (
                    <>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', minWidth: 160 }}>Trek Name</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', minWidth: 110 }}>Date</TableCell>
                    </>
                  ) : (
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                      {groupBy === 'trek' ? 'Trek Name' : 'Date / Period'}
                    </TableCell>
                  )}
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Total Intents</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Confirmed Paid</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Pending</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Cancelled</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Conv. %</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#fff8e1', color: '#b78103' }}>
                    Online (Razorpay)
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f3e5f5', color: '#6a1b9a' }}>
                    Cash
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#e0f2f1', color: '#00695c' }}>
                    QR / Bank
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#ed6c02' }}>
                    Total Collected
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Remaining Due</TableCell>
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={
                      activeTab === 'operations'
                        ? (groupBy === 'trek_date' ? 7 : 6)
                        : (compareYoY ? (groupBy === 'trek_date' ? 10 : 9) : (groupBy === 'trek_date' ? 12 : 11))
                    }
                    align="center"
                    sx={{ py: 6 }}
                  >
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : tableData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={
                      activeTab === 'operations'
                        ? (groupBy === 'trek_date' ? 7 : 6)
                        : (compareYoY ? (groupBy === 'trek_date' ? 10 : 9) : (groupBy === 'trek_date' ? 12 : 11))
                    }
                    align="center"
                    sx={{ py: 4 }}
                  >
                    <Typography variant="body2" color="text.secondary">No records found for this filter combination.</Typography>
                  </TableCell>
                </TableRow>
              ) : activeTab === 'operations' ? (
                compareYoY ? (
                  tableData.map((row, i) => (
                    <TableRow key={row.dimension_key || i} hover>
                      {groupBy === 'trek_date' ? (
                        <>
                          <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>
                            {row.trek_name || row.dimension_key?.split(' - ')[0] || row.dimension_key}
                          </TableCell>
                          <TableCell sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                            {row.date_label || row.dimension_key?.split(' - ')[1] || '-'}
                          </TableCell>
                        </>
                      ) : (
                        <TableCell sx={{ fontWeight: 600, fontSize: '12.5px' }}>{row.dimension_key}</TableCell>
                      )}

                      {/* Confirmed Paid Comparative */}
                      <TableCell align="right">
                        <Typography sx={{ color: 'success.main', fontWeight: 700, fontSize: '13px' }}>
                          {(Number(row.confirmed_count) || 0).toLocaleString('en-IN')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.25 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10.5px' }}>
                            LY: {(Number(row.yoy_confirmed_count) || 0).toLocaleString('en-IN')}
                          </Typography>
                          {row.confirmed_growth_pct !== null && row.confirmed_growth_pct !== undefined ? (
                            <Chip
                              label={`${row.confirmed_growth_pct > 0 ? '+' : ''}${row.confirmed_growth_pct}%`}
                              size="small"
                              color={Number(row.confirmed_growth_pct) >= 0 ? 'success' : 'error'}
                              variant={Number(row.confirmed_growth_pct) >= 0 ? 'filled' : 'outlined'}
                              sx={{ height: 18, fontSize: '10px', px: 0.5, fontWeight: 700 }}
                            />
                          ) : row.confirmed_count > 0 && (!row.yoy_confirmed_count || row.yoy_confirmed_count === 0) ? (
                            <Chip label="New" size="small" color="info" sx={{ height: 18, fontSize: '10px', px: 0.5 }} />
                          ) : null}
                        </Box>
                      </TableCell>

                      {/* Total Intents Comparative */}
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>
                          {(Number(row.total_registrations) || 0).toLocaleString('en-IN')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.25 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10.5px' }}>
                            LY: {(Number(row.yoy_total_registrations) || 0).toLocaleString('en-IN')}
                          </Typography>
                          {row.reg_growth_pct !== null && row.reg_growth_pct !== undefined && (
                            <Chip
                              label={`${row.reg_growth_pct > 0 ? '+' : ''}${row.reg_growth_pct}%`}
                              size="small"
                              color={Number(row.reg_growth_pct) >= 0 ? 'success' : 'error'}
                              variant="outlined"
                              sx={{ height: 16, fontSize: '9.5px', px: 0.25 }}
                            />
                          )}
                        </Box>
                      </TableCell>

                      {/* Pending Comparative */}
                      <TableCell align="right">
                        <Typography sx={{ fontSize: '12.5px' }}>
                          {(Number(row.pending_count) || 0).toLocaleString('en-IN')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px', display: 'block' }}>
                          LY: {(Number(row.yoy_pending_count) || 0).toLocaleString('en-IN')}
                        </Typography>
                      </TableCell>

                      {/* Cancelled Comparative */}
                      <TableCell align="right">
                        <Typography sx={{ color: 'error.main', fontSize: '12.5px' }}>
                          {(Number(row.cancelled_count) || 0).toLocaleString('en-IN')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px', display: 'block' }}>
                          LY: {(Number(row.yoy_cancelled_count) || 0).toLocaleString('en-IN')}
                        </Typography>
                      </TableCell>

                      {/* Conversion % */}
                      <TableCell align="right">
                        <Chip
                          label={`${row.conversion_rate || 0}%`}
                          size="small"
                          color={Number(row.conversion_rate) > 40 ? 'success' : Number(row.conversion_rate) > 20 ? 'warning' : 'default'}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '10.5px' }}
                        />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px', display: 'block', mt: 0.25 }}>
                          LY: {row.yoy_conversion_rate || 0}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  tableData.map((row, i) => (
                    <TableRow key={row.dimension_key || i} hover>
                      {groupBy === 'trek_date' ? (
                        <>
                          <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>
                            {row.trek_name || row.dimension_key?.split(' - ')[0] || row.dimension_key}
                          </TableCell>
                          <TableCell sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                            {row.date_label || row.dimension_key?.split(' - ')[1] || '-'}
                          </TableCell>
                        </>
                      ) : (
                        <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>{row.dimension_key}</TableCell>
                      )}
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '13px' }}>
                        {(Number(row.total_registrations) || 0).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ color: 'success.main', fontWeight: 700, fontSize: '13.5px' }}>
                          {(Number(row.confirmed_count) || 0).toLocaleString('en-IN')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary', fontSize: '13px' }}>
                        {(Number(row.pending_count) || 0).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'error.main', fontSize: '13px' }}>
                        {(Number(row.cancelled_count) || 0).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${row.conversion_rate || 0}%`}
                          size="small"
                          color={Number(row.conversion_rate) > 40 ? 'success' : Number(row.conversion_rate) > 20 ? 'warning' : 'default'}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '11px', fontWeight: 600 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )
              ) : compareYoY ? (
                tableData.map((row, i) => (
                  <TableRow key={row.dimension_key || i} hover>
                    {groupBy === 'trek_date' ? (
                      <>
                        <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>
                          {row.trek_name || row.dimension_key?.split(' - ')[0] || row.dimension_key}
                        </TableCell>
                        <TableCell sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                          {row.date_label || row.dimension_key?.split(' - ')[1] || '-'}
                        </TableCell>
                      </>
                    ) : (
                      <TableCell sx={{ fontWeight: 600, fontSize: '12.5px' }}>{row.dimension_key}</TableCell>
                    )}
                    
                    {/* Confirmed Paid Comparative */}
                    <TableCell align="right">
                      <Typography sx={{ color: 'success.main', fontWeight: 700, fontSize: '13px' }}>
                        {(Number(row.confirmed_count) || 0).toLocaleString('en-IN')}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.25 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10.5px' }}>
                          LY: {(Number(row.yoy_confirmed_count) || 0).toLocaleString('en-IN')}
                        </Typography>
                        {row.confirmed_growth_pct !== null && row.confirmed_growth_pct !== undefined ? (
                          <Chip
                            label={`${row.confirmed_growth_pct > 0 ? '+' : ''}${row.confirmed_growth_pct}%`}
                            size="small"
                            color={Number(row.confirmed_growth_pct) >= 0 ? 'success' : 'error'}
                            variant={Number(row.confirmed_growth_pct) >= 0 ? 'filled' : 'outlined'}
                            sx={{ height: 18, fontSize: '10px', px: 0.5, fontWeight: 700 }}
                          />
                        ) : row.confirmed_count > 0 && (!row.yoy_confirmed_count || row.yoy_confirmed_count === 0) ? (
                          <Chip label="New" size="small" color="info" sx={{ height: 18, fontSize: '10px', px: 0.5 }} />
                        ) : null}
                      </Box>
                    </TableCell>

                    {/* Total Collected Revenue Comparative */}
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 700, color: '#ed6c02', fontSize: '13px' }}>
                        {formatAdminInr(row.collected_revenue || 0)}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.25 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10.5px' }}>
                          LY: {formatAdminInr(row.yoy_collected_revenue || 0)}
                        </Typography>
                        {row.revenue_growth_pct !== null && row.revenue_growth_pct !== undefined ? (
                          <Chip
                            label={`${row.revenue_growth_pct > 0 ? '+' : ''}${row.revenue_growth_pct}%`}
                            size="small"
                            color={Number(row.revenue_growth_pct) >= 0 ? 'success' : 'error'}
                            variant={Number(row.revenue_growth_pct) >= 0 ? 'filled' : 'outlined'}
                            sx={{ height: 18, fontSize: '10px', px: 0.5, fontWeight: 700 }}
                          />
                        ) : row.collected_revenue > 0 && (!row.yoy_collected_revenue || row.yoy_collected_revenue === 0) ? (
                          <Chip label="New" size="small" color="info" sx={{ height: 18, fontSize: '10px', px: 0.5 }} />
                        ) : null}
                      </Box>
                    </TableCell>

                    {/* Total Intents */}
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>
                        {(Number(row.total_registrations) || 0).toLocaleString('en-IN')}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.25 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10.5px' }}>
                          LY: {(Number(row.yoy_total_registrations) || 0).toLocaleString('en-IN')}
                        </Typography>
                        {row.reg_growth_pct !== null && row.reg_growth_pct !== undefined && (
                          <Chip
                            label={`${row.reg_growth_pct > 0 ? '+' : ''}${row.reg_growth_pct}%`}
                            size="small"
                            color={Number(row.reg_growth_pct) >= 0 ? 'success' : 'error'}
                            variant="outlined"
                            sx={{ height: 16, fontSize: '9.5px', px: 0.25 }}
                          />
                        )}
                      </Box>
                    </TableCell>

                    {/* Online (Razorpay) */}
                    <TableCell align="right" sx={{ bgcolor: '#fffde7' }}>
                      <Typography sx={{ fontWeight: 600, color: '#e65100', fontSize: '12.5px' }}>
                        {formatAdminInr(row.online_revenue || 0)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px', display: 'block' }}>
                        LY: {formatAdminInr(row.yoy_online_revenue || 0)}
                      </Typography>
                    </TableCell>

                    {/* Cash */}
                    <TableCell align="right" sx={{ bgcolor: '#fbf7fd' }}>
                      <Typography sx={{ fontWeight: 600, color: '#4a148c', fontSize: '12.5px' }}>
                        {formatAdminInr(row.office_revenue || 0)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px', display: 'block' }}>
                        LY: {formatAdminInr(row.yoy_office_revenue || 0)}
                      </Typography>
                    </TableCell>

                    {/* QR / Bank */}
                    <TableCell align="right" sx={{ bgcolor: '#e0f2f1' }}>
                      <Typography sx={{ fontWeight: 600, color: '#00695c', fontSize: '12.5px' }}>
                        {formatAdminInr(row.qr_revenue || 0)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px', display: 'block' }}>
                        LY: {formatAdminInr(row.yoy_qr_revenue || 0)}
                      </Typography>
                    </TableCell>

                    {/* Conversion % */}
                    <TableCell align="right">
                      <Chip
                        label={`${row.conversion_rate || 0}%`}
                        size="small"
                        color={Number(row.conversion_rate) > 40 ? 'success' : Number(row.conversion_rate) > 20 ? 'warning' : 'default'}
                        variant="outlined"
                        sx={{ height: 20, fontSize: '10.5px' }}
                      />
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px', display: 'block', mt: 0.25 }}>
                        LY: {row.yoy_conversion_rate || 0}%
                      </Typography>
                    </TableCell>

                    {/* Remaining Due */}
                    <TableCell align="right">
                      <Typography sx={{ color: 'text.secondary', fontSize: '12px' }}>
                        {formatAdminInr(row.remaining_due || 0)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px', display: 'block' }}>
                        LY: {formatAdminInr(row.yoy_remaining_due || 0)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                tableData.map((row, i) => (
                  <TableRow key={row.dimension_key || i} hover>
                    {groupBy === 'trek_date' ? (
                      <>
                        <TableCell sx={{ fontWeight: 600, fontSize: '13px' }}>
                          {row.trek_name || row.dimension_key?.split(' - ')[0] || row.dimension_key}
                        </TableCell>
                        <TableCell sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                          {row.date_label || row.dimension_key?.split(' - ')[1] || '-'}
                        </TableCell>
                      </>
                    ) : (
                      <TableCell sx={{ fontWeight: 600 }}>{row.dimension_key}</TableCell>
                    )}
                    <TableCell align="right">{(Number(row.total_registrations) || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>
                       {(Number(row.confirmed_count) || 0).toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell align="right">{(Number(row.pending_count) || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      {(Number(row.cancelled_count) || 0).toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${row.conversion_rate || 0}%`}
                        size="small"
                        color={Number(row.conversion_rate) > 40 ? 'success' : Number(row.conversion_rate) > 20 ? 'warning' : 'default'}
                        variant="outlined"
                        sx={{ height: 20, fontSize: '11px' }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#e65100', bgcolor: '#fffde7' }}>
                      {formatAdminInr(row.online_revenue || 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#4a148c', bgcolor: '#fbf7fd' }}>
                      {formatAdminInr(row.office_revenue || 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#00695c', bgcolor: '#e0f2f1' }}>
                      {formatAdminInr(row.qr_revenue || 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#ed6c02' }}>
                      {formatAdminInr(row.collected_revenue || 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>
                      {formatAdminInr(row.remaining_due || 0)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Server Pagination Controls */}
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={pagination.totalRows || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>
    </Box>
  );
}
